// src/pages/WaitingPaymentPage.js
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, WalletOutlined } from '@ant-design/icons';
import { Button, Result, Spin } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom'; // Added useLocation
import apis from '../../apis';

const WaitingPaymentPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const passedPaymentLink = location.state?.paymentLink;
    const isPublicTicket = location.state?.isPublicTicket;

    const [status, setStatus] = useState('PENDING');
    const [loading, setLoading] = useState(true);
    const [lockExpiresAt, setLockExpiresAt] = useState(null);
    const [timeLeftStr, setTimeLeftStr] = useState('');
    const [paymentLink, setPaymentLink] = useState(passedPaymentLink || '');
    const [isPublicTicketDetected, setIsPublicTicketDetected] = useState(Boolean(isPublicTicket));
    const stopPolling = useRef(false);

    useEffect(() => {
        if (!id) return;
        stopPolling.current = false;
        setStatus('PENDING');
        setLoading(true);
        setLockExpiresAt(null);
        setTimeLeftStr('');
        let canceled = false;

        const checkStatus = async () => {
            if (stopPolling.current) return;

            try {
                let currentStatus = 'PENDING';
                let expiresAt = null;

                const shouldProbePublicBooking = isPublicTicket || !location.state;
                if (shouldProbePublicBooking) {
                    try {
                        const response = await apis.publicTicket.getBookingStatus(id);
                        currentStatus = response.data.paymentStatus;
                        expiresAt = response.data.lockExpiresAt;
                        if (response.data.paymentUrl) setPaymentLink(response.data.paymentUrl);
                        setIsPublicTicketDetected(true);
                    } catch (publicBookingError) {
                        if (isPublicTicket) throw publicBookingError;
                        const response = await apis.payment.getRegistrantStatus(id);
                        currentStatus = response.data.paymentStatus;
                    }
                } else {
                    const response = await apis.payment.getRegistrantStatus(id);
                    currentStatus = response.data.paymentStatus;
                }

                if (canceled || stopPolling.current) return;
                if (expiresAt) setLockExpiresAt(expiresAt);
                if (currentStatus === 'PAID') {
                    setStatus('PAID');
                    setLoading(false);
                    stopPolling.current = true;
                } else if (currentStatus === 'expired') {
                    setStatus('EXPIRED');
                    setLoading(false);
                    stopPolling.current = true;
                } else if (currentStatus === 'failed') {
                    setStatus('FAILED');
                    setLoading(false);
                    stopPolling.current = true;
                }
            } catch (error) {
                console.error("Polling error", error);
            }
        };

        checkStatus();
        const intervalId = setInterval(checkStatus, 3000);

        return () => {
            canceled = true;
            stopPolling.current = true;
            clearInterval(intervalId);
        };
    }, [id, isPublicTicket, location.state]);

    // Timer effect
    useEffect(() => {
        if (!lockExpiresAt || status !== 'PENDING') return;

        const intervalId = setInterval(() => {
            const now = new Date().getTime();
            const expiry = new Date(lockExpiresAt).getTime();
            const distance = expiry - now;

            if (distance <= 0) {
                clearInterval(intervalId);
                setTimeLeftStr('00:00');
                return;
            }

            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);
            setTimeLeftStr(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        }, 1000);

        return () => clearInterval(intervalId);
    }, [lockExpiresAt, status]);

    const handleFinish = () => {
        navigate('/');
    };

    // Helper to open link manually
    const handleManualPay = () => {
        if (paymentLink) {
            window.open(paymentLink, '_blank');
        }
    };

    if (status === 'PAID') {
        return (
            <div className="container" style={{ height: '100vh', paddingTop: '100px', textAlign: 'center', color: '#e5cc92' }}>
                <Result
                    status="success"
                    icon={<CheckCircleOutlined style={{ color: '#e5cc92' }} />}
                    title={<span style={{ color: '#e5cc92' }}>Payment Successful!</span>}
                    subTitle={<span style={{ color: 'white' }}>Thank you for your payment. Your registration is now complete.</span>}
                    extra={[
                        <Button type="primary" key="console" onClick={handleFinish}
                            style={{ backgroundColor: '#e5cc92', color: 'black', borderColor: '#e5cc92' }}>
                            Back to Home
                        </Button>,
                    ]}
                    style={{ backgroundColor: '#1E1E1E', borderRadius: '8px', padding: '40px' }}
                />
            </div>
        );
    }

    if (status === 'EXPIRED' || status === 'FAILED') {
        return (
            <div className="container" style={{ height: '100vh', paddingTop: '100px', textAlign: 'center', color: '#e5cc92' }}>
                <Result
                    status="error"
                    icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                    title={<span style={{ color: '#ff4d4f' }}>{status === 'FAILED' ? 'Booking Failed / Pemesanan Gagal' : 'Time Expired'}</span>}
                    subTitle={<span style={{ color: 'white' }}>{status === 'FAILED'
                        ? 'Checkout could not be completed. Do not pay this invoice. If you have already paid, contact APCS with your booking ID. / Pemesanan tidak berhasil. Jangan bayar tagihan ini. Jika sudah membayar, hubungi APCS dengan ID pemesanan Anda.'
                        : 'Your seat reservation has expired. Please check your payment status before booking again.'}</span>}
                    extra={[
                        <Button type="primary" key="console" onClick={handleFinish}
                            style={{ backgroundColor: '#e5cc92', color: 'black', borderColor: '#e5cc92' }}>
                            Back to Home
                        </Button>,
                    ]}
                    style={{ backgroundColor: '#1E1E1E', borderRadius: '8px', padding: '40px' }}
                />
            </div>
        );
    }

    return (
        <div className="container" style={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#121212',
            color: '#e5cc92',
            padding: '20px'
        }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#e5cc92' }} spin />} />

            <h2 style={{ marginTop: 30, color: '#e5cc92' }}>Waiting for Payment...</h2>

            {(isPublicTicket || isPublicTicketDetected) && timeLeftStr && (
                <div style={{ margin: '15px 0', padding: '10px 20px', backgroundColor: '#2a2a2a', borderRadius: '8px', border: '1px solid #444', textAlign: 'center' }}>
                    <h3 style={{ color: '#ff4d4f', margin: 0, fontWeight: 'bold', fontSize: '24px' }}>
                        {timeLeftStr}
                    </h3>
                    <p style={{ color: '#aaa', margin: 0, fontSize: '12px' }}>Time remaining to complete payment</p>
                </div>
            )}

            {(isPublicTicket || isPublicTicketDetected) && (
                <div style={{ margin: '10px 0 20px 0', padding: '12px 16px', backgroundColor: 'rgba(235, 188, 100, 0.1)', borderRadius: '8px', border: '1px solid rgba(235, 188, 100, 0.3)', color: '#e5cc92', fontSize: '14px', maxWidth: '500px', textAlign: 'center' }}>
                    <strong>Notice:</strong> All ticket sales are final and <strong>strictly non-refundable</strong> once paid. Please ensure your seat selections and details are correct before completing your payment.
                </div>
            )}

            <p style={{ color: '#aaa', textAlign: 'center', maxWidth: 500 }}>
                We have attempted to open a new tab for you to complete your payment.<br />
                You can also check your Email or WhatsApp for the invoice.
            </p>

            {/* --- Backup Payment Button --- */}
            {paymentLink && (
                <div style={{ marginTop: 20, marginBottom: 20, textAlign: 'center' }}>
                    <p style={{ color: '#fff', marginBottom: 10 }}>Pop-up blocked?</p>
                    <Button
                        type="primary"
                        icon={<WalletOutlined />}
                        onClick={handleManualPay}
                        size="large"
                        style={{
                            backgroundColor: '#e5cc92',
                            color: '#000',
                            borderColor: '#e5cc92',
                            fontWeight: 'bold'
                        }}
                    >
                        Pay Now / Open Invoice
                    </Button>
                </div>
            )}

            <p style={{ color: '#aaa', textAlign: 'center', marginTop: 20 }}>
                <strong>Please do not close this page.</strong><br />
                It will update automatically once we receive the confirmation.
            </p>

            <p style={{ fontSize: 12, marginTop: 50, color: '#666' }}>
                Transaction ID: {id}
            </p>
        </div>
    );
};

export default WaitingPaymentPage;
